/**
 * Google Tag Manager integration with Consent Mode v2
 * Provides privacy-first analytics and advertising with regional compliance
 */

// GTM Configuration
const GTM_CONFIG = {
  containerId: process.env.REACT_APP_GTM_CONTAINER_ID || 'GTM-XXXXXXX',
  
  // Privacy-first default consent state
  defaultConsent: {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'functionality_storage': 'granted',
    'security_storage': 'granted',
    'wait_for_update': 2000
  },
  
  // Regional consent defaults for compliance
  regionalDefaults: {
    'EU': {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    },
    'US-CA': {
      'analytics_storage': 'granted',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    },
    'GB': {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    }
  }
};

/**
 * Initialize Google Tag Manager with Consent Mode v2
 * Must be called before GTM container loads
 */
export function initializeGTM() {
  // Ensure we're in browser environment
  if (typeof window === 'undefined') {
    console.warn('GTM: Cannot initialize in non-browser environment');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  
  const gtag = (...args) => {
    window.dataLayer.push(args);
  };
  
  // Make gtag globally available
  window.gtag = gtag;

  // Set default consent before GTM loads (privacy-first approach)
  gtag('consent', 'default', GTM_CONFIG.defaultConsent);

  // Set regional defaults based on user location
  setRegionalConsentDefaults(gtag);

  // Load GTM container
  loadGTMContainer();

  // Set up consent update listener
  setupConsentUpdateListener(gtag);

  console.log('GTM: Initialized with Consent Mode v2 support');
}

/**
 * Set regional consent defaults for compliance
 */
function setRegionalConsentDefaults(gtag) {
  try {
    // Detect user region (simplified detection)
    const userRegion = detectUserRegion();
    
    if (GTM_CONFIG.regionalDefaults[userRegion]) {
      gtag('consent', 'default', {
        ...GTM_CONFIG.regionalDefaults[userRegion],
        'region': [userRegion]
      });
      
      console.log(`GTM: Applied regional defaults for ${userRegion}`);
    }
  } catch (error) {
    console.warn('GTM: Error setting regional defaults:', error);
  }
}

/**
 * Detect user region for compliance
 * This is a simplified implementation - consider using a proper geolocation service
 */
function detectUserRegion() {
  // Check for California (CCPA)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone.includes('Los_Angeles') || timezone.includes('Pacific')) {
    return 'US-CA';
  }

  // Check for EU countries (GDPR)
  const language = navigator.language || navigator.userLanguage;
  const euLanguages = ['de', 'fr', 'es', 'it', 'nl', 'pl', 'sv', 'da', 'fi', 'no'];
  if (euLanguages.some(lang => language.startsWith(lang))) {
    return 'EU';
  }

  // Check for UK (ePrivacy)
  if (language.startsWith('en-GB')) {
    return 'GB';
  }

  return 'GLOBAL';
}

/**
 * Load GTM container script
 */
function loadGTMContainer() {
  if (!GTM_CONFIG.containerId || GTM_CONFIG.containerId === 'GTM-XXXXXXX') {
    console.warn('GTM: No valid container ID provided. Set REACT_APP_GTM_CONTAINER_ID environment variable.');
    return;
  }

  // Add GTM script to head
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONFIG.containerId}`;
  
  // Add GTM initialization
  script.onload = () => {
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      'event': 'gtm.js'
    });
    console.log('GTM: Container loaded successfully');
  };

  script.onerror = () => {
    console.error('GTM: Failed to load container');
  };

  document.head.appendChild(script);

  // Add noscript fallback to body
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONFIG.containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  
  if (document.body) {
    document.body.appendChild(noscript);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      document.body.appendChild(noscript);
    });
  }
}

/**
 * Set up listener for consent updates from consent manager
 */
function setupConsentUpdateListener(gtag) {
  // Listen for consent updates from our consent manager
  window.addEventListener('consentUpdate', (event) => {
    const { consentData } = event.detail;
    
    // Update GTM consent
    gtag('consent', 'update', {
      'analytics_storage': consentData.analytics_storage,
      'ad_storage': consentData.ad_storage,
      'ad_user_data': consentData.ad_user_data,
      'ad_personalization': consentData.ad_personalization
    });

    // Track consent update event
    gtag('event', 'consent_update', {
      'consent_analytics': consentData.analytics_storage,
      'consent_ads': consentData.ad_storage,
      'consent_personalization': consentData.ad_personalization,
      'consent_user_data': consentData.ad_user_data,
      'timestamp': new Date().toISOString()
    });

    console.log('GTM: Consent updated', consentData);
  });
}

/**
 * Send custom event to GTM
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Event parameters
 */
export function sendGTMEvent(eventName, eventData = {}) {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GTM: gtag not available');
    return;
  }

  window.gtag('event', eventName, {
    ...eventData,
    'timestamp': new Date().toISOString()
  });

  console.log(`GTM: Event sent - ${eventName}`, eventData);
}

/**
 * Update consent in GTM
 * @param {Object} consentData - Consent preferences
 */
export function updateGTMConsent(consentData) {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GTM: gtag not available');
    return;
  }

  window.gtag('consent', 'update', consentData);
  console.log('GTM: Consent updated externally', consentData);
}

/**
 * Send consent preferences to GTM
 * @param {Object} preferences - User consent preferences
 */
export function sendConsentPreferences(preferences) {
  sendGTMEvent('user_consent_preferences', {
    'analytics_consent': preferences.analytics_storage,
    'advertising_consent': preferences.ad_storage,
    'personalization_consent': preferences.ad_personalization,
    'user_data_consent': preferences.ad_user_data,
    'consent_method': 'banner_interaction'
  });
}

/**
 * Check if GTM is loaded and ready
 * @returns {boolean} True if GTM is loaded
 */
export function isGTMLoaded() {
  return typeof window !== 'undefined' && 
         window.dataLayer && 
         window.gtag && 
         window.google_tag_manager;
}

/**
 * Get GTM configuration
 * @returns {Object} Current GTM configuration
 */
export function getGTMConfig() {
  return { ...GTM_CONFIG };
}

/**
 * Enable debug mode for GTM (development only)
 */
export function enableGTMDebug() {
  if (process.env.NODE_ENV === 'development') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      'event': 'gtm.js',
      'gtm.debug': true
    });
    console.log('GTM: Debug mode enabled');
  }
}

// Enhanced privacy features for Consent Mode v2
export const GTMPrivacyFeatures = {
  /**
   * Send cookieless ping for basic analytics
   */
  sendCookielessPing() {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        'send_to': GTM_CONFIG.containerId,
        'client_id': this.generateClientId(),
        'anonymize_ip': true,
        'cookie_flags': 'SameSite=None;Secure'
      });
    }
  },

  /**
   * Generate privacy-focused client ID
   */
  generateClientId() {
    const sessionId = sessionStorage.getItem('gtm_session_id');
    if (sessionId) {
      return sessionId;
    }

    const newId = 'client_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('gtm_session_id', newId);
    return newId;
  },

  /**
   * Clear all GTM data (for privacy compliance)
   */
  clearAllData() {
    // Clear GTM-related cookies
    const gtmCookies = ['_ga', '_gid', '_gat', '_gtag'];
    gtmCookies.forEach(cookie => {
      document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    // Clear local storage
    const gtmKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('_ga') || key.startsWith('gtm')
    );
    gtmKeys.forEach(key => localStorage.removeItem(key));

    // Clear session storage
    sessionStorage.removeItem('gtm_session_id');

    console.log('GTM: All tracking data cleared');
  }
};

const GTMIntegration = {
  initializeGTM,
  sendGTMEvent,
  updateGTMConsent,
  sendConsentPreferences,
  isGTMLoaded,
  getGTMConfig,
  enableGTMDebug,
  GTMPrivacyFeatures
};

export default GTMIntegration;